import { Layout } from '@/components/Layout';
import { Card } from '@/components/ui/card';
import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import { usePageSeo } from '@/hooks/usePageSeo';

const AllStates = () => {
  usePageSeo({
    title: 'Property Tax Appeal Guides by State (2026) | PropertyTaxHero',
    description: 'Complete 2026 guide to property tax appeal procedures, deadlines, and requirements for all 50 states. Find your state\'s specific appeal process with PropertyTaxHero.',
    path: '/all-states',
    schema: {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: 'Home', item: 'https://www.propertytaxhero.com/' },
        { '@type': 'ListItem', position: 2, name: 'State Guides', item: 'https://www.propertytaxhero.com/all-states' },
      ],
    },
  });

  const states = [
    { name: 'Alabama', path: '/alabama' }, { name: 'Alaska', path: '/alaska' }, { name: 'Arizona', path: '/arizona' },
    { name: 'Arkansas', path: '/arkansas' }, { name: 'California', path: '/california' }, { name: 'Colorado', path: '/colorado' },
    { name: 'Connecticut', path: '/connecticut' }, { name: 'Delaware', path: '/delaware' }, { name: 'District of Columbia', path: '/district-of-columbia' },
    { name: 'Florida', path: '/florida' }, { name: 'Georgia', path: '/georgia' }, { name: 'Hawaii', path: '/hawaii' },
    { name: 'Idaho', path: '/idaho' }, { name: 'Illinois', path: '/illinois' }, { name: 'Indiana', path: '/indiana' },
    { name: 'Iowa', path: '/iowa' }, { name: 'Kansas', path: '/kansas' }, { name: 'Kentucky', path: '/kentucky' },
    { name: 'Louisiana', path: '/louisiana' }, { name: 'Maine', path: '/maine' }, { name: 'Maryland', path: '/maryland' },
    { name: 'Massachusetts', path: '/massachusetts' }, { name: 'Michigan', path: '/michigan' }, { name: 'Minnesota', path: '/minnesota' },
    { name: 'Mississippi', path: '/mississippi' }, { name: 'Missouri', path: '/missouri' }, { name: 'Montana', path: '/montana' },
    { name: 'Nebraska', path: '/nebraska' }, { name: 'Nevada', path: '/nevada' }, { name: 'New Hampshire', path: '/new-hampshire' },
    { name: 'New Jersey', path: '/new-jersey' }, { name: 'New Mexico', path: '/new-mexico' }, { name: 'New York', path: '/new-york' },
    { name: 'North Carolina', path: '/north-carolina' }, { name: 'North Dakota', path: '/north-dakota' }, { name: 'Ohio', path: '/ohio' },
    { name: 'Oklahoma', path: '/oklahoma' }, { name: 'Oregon', path: '/oregon' }, { name: 'Pennsylvania', path: '/pennsylvania' },
    { name: 'Rhode Island', path: '/rhode-island' }, { name: 'South Carolina', path: '/south-carolina' }, { name: 'South Dakota', path: '/south-dakota' },
    { name: 'Tennessee', path: '/tennessee' }, { name: 'Texas', path: '/texas' }, { name: 'Utah', path: '/utah' },
    { name: 'Vermont', path: '/vermont' }, { name: 'Virginia', path: '/virginia' }, { name: 'Washington', path: '/washington' },
    { name: 'West Virginia', path: '/west-virginia' }, { name: 'Wisconsin', path: '/wisconsin' }, { name: 'Wyoming', path: '/wyoming' },
  ];

  return (
    <Layout>
      <div className="min-h-screen bg-gradient-to-br from-tax-backgroundLight to-white">
        <div className="container mx-auto px-4 py-8 max-w-6xl">
          <nav aria-label="Breadcrumb" className="text-sm text-tax-neutralGray mb-6">
            <Link to="/" className="hover:text-tax-brightBlue">Home</Link> &gt; <span>State Guides</span>
          </nav>
          <div className="text-center mb-12">
            <h1 className="text-4xl md:text-5xl font-bold text-tax-deepBlue mb-6">Property Tax Appeal Guides by State (2026)</h1>
            <p className="text-xl text-tax-neutralGray max-w-3xl mx-auto leading-relaxed">Find detailed property tax appeal procedures, deadlines, and requirements for your state. Select your state below to get started.</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-12">
            {states.map((state) => (
              <Card key={state.path} className="p-6 hover:shadow-lg transition-shadow">
                <Link to={state.path} className="group flex items-center justify-between">
                  <div>
                    <h3 className="text-lg font-semibold text-tax-deepBlue group-hover:text-tax-brightBlue transition-colors">{state.name}</h3>
                    <p className="text-sm text-tax-neutralGray mt-1">View appeal procedures & deadlines</p>
                  </div>
                  <ArrowRight className="h-5 w-5 text-tax-neutralGray group-hover:text-tax-brightBlue transition-colors" />
                </Link>
              </Card>
            ))}
          </div>
          <div className="text-center">
            <Card className="p-8 bg-tax-backgroundLight">
              <h3 className="text-2xl font-bold text-tax-deepBlue mb-4">Need Help with Your Property Tax Appeal?</h3>
              <p className="text-lg text-tax-neutralGray mb-6">Get personalized guidance and deadline reminders for your specific property</p>
              <Link to="/">
                <button className="bg-tax-deepBlue hover:bg-tax-brightBlue text-white px-8 py-3 rounded-xl font-semibold transition-colors">
                  Start Your Free Assessment<ArrowRight className="ml-2 h-4 w-4 inline" />
                </button>
              </Link>
            </Card>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default AllStates;
